/**
 * MarsPartitionConfig Component
 * 개별 MARS 파티션의 Problem Type / Problem Option / Restart Source 설정
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import type { PartitionFormData, MARSConfig, MARSProblemType, MARSProblemOption } from '@/types/project';
import { DEFAULT_MARS_CONFIG } from './marsConfigDefault';
import RestartSourceSelector from './RestartSourceSelector';
import { ScopeChips, RoleChip } from '@/features/scope/ScopeSelector';

interface MarsPartitionConfigProps {
  partition: PartitionFormData;
  onChange: (updated: PartitionFormData) => void;
}


const MarsPartitionConfig: React.FC<MarsPartitionConfigProps> = ({ partition, onChange }) => {
  const config = partition.marsConfig ?? DEFAULT_MARS_CONFIG;
  const isRestart = config.problemType === 'RESTART';

  const updateConfig = (updates: Partial<MARSConfig>) => {
    const newConfig: MARSConfig = { ...config, ...updates };

    if (updates.problemType === 'NEW') {
      delete newConfig.restartSource;
    }

    onChange({ ...partition, marsConfig: newConfig });
  };

  const handleProblemTypeChange = (_: React.MouseEvent, value: MARSProblemType | null) => {
    if (value && value !== config.problemType) updateConfig({ problemType: value });
  };

  const handleProblemOptionChange = (_: React.MouseEvent, value: MARSProblemOption | null) => {
    if (value && value !== config.problemOption) updateConfig({ problemOption: value });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderColor: 'divider',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        {/* 헤더: 파티션명 + 스코프 텍스트 */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {partition.name || 'Unnamed Partition'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <RoleChip role={partition.role} />
            <ScopeChips scopes={partition.scope} />
          </Box>
        </Box>

        {/* Problem Type + Problem Option */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              Problem Type
            </Typography>
            <ToggleButtonGroup
              value={config.problemType}
              exclusive
              onChange={handleProblemTypeChange}
              size="small"
            >
              {/* 카드 100 문제 유형 — 항목 의미를 툴팁으로 설명 (#283, QA) */}
              <Tooltip title="새 계산 — 덱의 초기 조건에서 처음부터 해석을 시작합니다">
                <ToggleButton value="NEW" sx={{ px: 2, textTransform: 'none' }}>
                  NEW
                </ToggleButton>
              </Tooltip>
              <Tooltip title="재시작 계산 — 기존 실행의 재시작 파일(rstplt)에서 이어서 해석합니다. 아래에서 소스 실행을 선택합니다">
                <ToggleButton value="RESTART" sx={{ px: 2, textTransform: 'none' }}>
                  RESTART
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              Problem Option
            </Typography>
            <ToggleButtonGroup
              value={config.problemOption}
              exclusive
              onChange={handleProblemOptionChange}
              size="small"
            >
              <Tooltip title="정상상태(steady-state) 탐색 — 시간 전진으로 계통을 정상상태에 수렴시키는 모드입니다">
                <ToggleButton value="STDY-ST" sx={{ px: 2, textTransform: 'none' }}>
                  STDY-ST
                </ToggleButton>
              </Tooltip>
              <Tooltip title="과도(transient) 해석 — 시간에 따른 계통 거동을 계산하는 일반 해석 모드입니다">
                <ToggleButton value="TRANSNT" sx={{ px: 2, textTransform: 'none' }}>
                  TRANSNT
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* RESTART Source (조건부 표시) */}
        <Collapse in={isRestart}>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Restart Source
            </Typography>
            <RestartSourceSelector
              value={config.restartSource}
              onChange={(source) => updateConfig({ restartSource: source })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              RESTART 모드에서는 비응축가스 종류/질량분율, 초기 시각, 수력학적 시스템 설정을 에디터에서 변경할 수 없습니다 — 재시작 파일의 값을 잇습니다.
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MarsPartitionConfig;
